import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { StorageService, UtilsService } from 'src/app/services';
import { images } from '../../../../images';
import {
  CommunicationType,
  CommunicationUserType,
  CustomToggleTone,
  FilterField,
  FilterType,
  UserRole,
} from 'src/app/models/enums';
import { ICustomToggleOptions, IFilterPanelValues } from 'src/app/models';
import { FILTER_PANEL_ADAPTERS } from 'src/app/shared/filter-panel/filterPanelAdapters';
import {
  deserializeMessagesFilters,
  deserializeSuggestionsFilters,
  parseMessagesFiltersFromStorage,
  parseSuggestionsFiltersFromStorage,
  toMessagesPanelFilters,
  toSuggestionsPanelFilters,
} from 'src/app/filters';

@Component({
  selector: 'app-communication-search-bar',
  templateUrl: './communication-search-bar.component.html',
  styleUrl: './communication-search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CommunicationSearchBarComponent
  implements OnInit, OnChanges, OnDestroy
{
  private queryParamsSub?: Subscription;

  @Input() searchValue = '';
  @Input() placeholder = '';
  @Input() searching = false;
  @Input() communicationType = CommunicationType.SUGGESTIONS;
  @Input() communicationUserType = CommunicationUserType.USERS;
  @Output() addCommunicationEventEmitter = new EventEmitter();
  @Output() filter = new EventEmitter<any>();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() changeCommunicationUserTypeEmitter = new EventEmitter<CommunicationUserType>();
  @Output() changeCommunicationTypeEmitter = new EventEmitter();

  public filterOpened = false;
  public filtersApplied = 0;
  public filterTypes = FilterType;
  public messageFilterFields: FilterField[] = [
    FilterField.CLIENTS,
    FilterField.SENDERS,
    FilterField.RECEIVERS,
  ];
  public suggestionFilterFields: FilterField[] = [FilterField.CLIENTS, FilterField.AREAS_SERVICES_EXPEDITIONS];

  public images = images;

  public CommunicationTypes = CommunicationType;
  public CommunicationUserTypes = CommunicationUserType;
  readonly communicationTypeToggleOptions: ICustomToggleOptions<CommunicationType> = [
    {
      value: CommunicationType.SUGGESTIONS,
      image: images.suggestions.suggestionsIcon,
      descriptionKey: 'communications.types.suggestions',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: CommunicationType.MESSAGES,
      image: images.suggestions.messagesIcon,
      descriptionKey: 'communications.types.messages',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  readonly communicationUserTypeToggleOptions: ICustomToggleOptions<CommunicationUserType> = [
    {
      value: CommunicationUserType.USERS,
      image: images.suggestions.user,
      descriptionKey: 'users.userTypes.users',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: CommunicationUserType.DRIVERS,
      image: images.suggestions.driver,
      descriptionKey: 'users.userTypes.drivers',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  public adminRole: UserRole;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.getRole();
    this.updateFilters();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => {
      this.updateFilters();
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['communicationType'] || changes['communicationUserType'])
      this.updateFilters();
  }

  async getRole() {
    this.adminRole = await this.storageService.getRole();
    this.messageFilterFields = this.buildMessageFilterFields();
  }

  private buildMessageFilterFields(): FilterField[] {
    const fields = [FilterField.CLIENTS, FilterField.SENDERS, FilterField.RECEIVERS];
    if (this.adminRole === UserRole.SUPERADMIN || this.adminRole === UserRole.ADMIN) {
      fields.push(FilterField.MESSAGE_EVENTS, FilterField.MESSAGE_ORIGIN);
    }
    if (this.adminRole === UserRole.SUPERADMIN) fields.push(FilterField.MESSAGE_ADMIN_ALERTS);
    return fields;
  }

  search(event: any) {
    this.searchEventEmitter.emit(event);
  }

  changeCommunicationUserType(userType: CommunicationUserType) {
    this.changeCommunicationUserTypeEmitter.emit(userType);
  }

  toggleCommunicationType() {
    this.changeCommunicationTypeEmitter.emit(
      this.communicationType === CommunicationType.SUGGESTIONS ? CommunicationType.MESSAGES : CommunicationType.SUGGESTIONS,
    );
  }

  closeFilter() {
    this.filterOpened = false;
  }

  onMessagesFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.MESSAGES]!.toFilters(values));
  }

  onSuggestionsFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.SUGGESTIONS]!.toFilters(values));
  }

  async updateFilters() {
    const params = this.activatedRoute.snapshot.queryParamMap;
    let filtersForCount: any | null = null;
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      const suggestionsFilters =
        params.keys.length > 0
          ? deserializeSuggestionsFilters(params)
          : parseSuggestionsFiltersFromStorage(
              await this.storageService.getSuggestionsFilters(),
            );
      filtersForCount = suggestionsFilters ? toSuggestionsPanelFilters(suggestionsFilters) : null;
    } else if (this.communicationType === CommunicationType.MESSAGES) {
      const messagesFilters =
        params.keys.length > 0
          ? deserializeMessagesFilters(params)
          : parseMessagesFiltersFromStorage(
              await this.storageService.getMessagesFilters(),
            );
      filtersForCount = messagesFilters ? toMessagesPanelFilters(messagesFilters) : null;
    }
    this.filtersApplied = filtersForCount
      ? this.utilsService.getFiltersCount(filtersForCount)
      : 0;
  }
}
